import { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

import { useScopeParams } from "@/hooks/useScopeParams";
import { useUser } from "@/contexts/UserContext";
import { apiFetch, apiFetchJson } from "@/utils/apiFetch";
import DiffModal from "./DiffModal";
import type {
  QueueItemDetail,
  Comment,
  AuditEntry,
  MasteryAction,
} from "./DiffModal";

async function fetchMasteryItemDetail(
  itemId: string,
): Promise<QueueItemDetail> {
  return apiFetchJson<QueueItemDetail>(
    `${import.meta.env.VITE_API_URL}/data/portfolio/mastery-queue/item/${encodeURIComponent(itemId)}/`,
  );
}

async function patchMasteryItem(
  itemId: string,
  patch: {
    priority?: string | null;
    status?: string | null;
    action?: string;
    notes?: string;
  },
): Promise<void> {
  await apiFetch(
    `${import.meta.env.VITE_API_URL}/data/portfolio/mastery-queue/${encodeURIComponent(itemId)}/`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(patch),
    },
  );
}

async function fetchAuditHistory(itemId: string): Promise<AuditEntry[]> {
  return apiFetchJson<AuditEntry[]>(
    `${import.meta.env.VITE_API_URL}/data/portfolio/mastery-queue/${encodeURIComponent(itemId)}/history/`,
  );
}

type Props = {
  /** ID of the mastery queue item to show. null = modal closed. */
  itemId: string | null;
  onClose: () => void;
};

/**
 * Opens the shared DiffModal for a single mastery queue item by ID,
 * hydrated from the item detail endpoint.
 */
export default function MasteryItemModal({ itemId, onClose }: Props) {
  const { scope } = useScopeParams();
  const { userInfo } = useUser();
  const queryClient = useQueryClient();
  const userFullName = userInfo
    ? `${userInfo.first_name} ${userInfo.last_name}`.trim()
    : "";

  const [activeItemId, setActiveItemId] = useState<string | null>(null);
  const effectiveItemId = activeItemId ?? itemId;

  const [localOverrides, setLocalOverrides] = useState<
    Partial<QueueItemDetail>
  >({});
  const [draft, setDraft] = useState("");

  const { data: itemDetail, isLoading } = useQuery({
    queryKey: ["mastery-item-detail", scope, effectiveItemId],
    queryFn: () => fetchMasteryItemDetail(effectiveItemId!),
    enabled: !!scope && !!effectiveItemId,
    staleTime: 30_000,
  });
  const modalItem = itemDetail ? { ...itemDetail, ...localOverrides } : null;

  const { data: auditHistoryData, isLoading: auditHistoryLoading } = useQuery({
    queryKey: ["mastery-audit", scope, effectiveItemId],
    queryFn: () => fetchAuditHistory(effectiveItemId!),
    enabled: !!scope && !!effectiveItemId,
    staleTime: 30_000,
  });
  const auditHistory = useMemo(
    () => auditHistoryData ?? [],
    [auditHistoryData],
  );

  const comments: Comment[] = useMemo(
    () =>
      auditHistory
        .filter((e) => e.action === "note_added" && e.notes)
        .map((e, i) => ({
          id: `n-${i}-${e.performed_at}`,
          author: e.performed_by ?? "Unknown",
          timestamp: e.performed_at,
          text: e.notes,
        }))
        .reverse(),
    [auditHistory],
  );

  const statusMutation = useMutation({
    mutationFn: (patch: {
      priority?: string | null;
      status?: string | null;
      action?: string;
      notes?: string;
    }) => patchMasteryItem(effectiveItemId!, patch),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["mastery-item-detail", scope, effectiveItemId],
      });
      queryClient.invalidateQueries({
        queryKey: ["mastery-audit", scope, effectiveItemId],
      });
      queryClient.invalidateQueries({
        queryKey: ["mastery-queue-counts", scope],
      });
    },
  });

  const handlePriorityChange = (val: string | null) => {
    setLocalOverrides((prev) => ({
      ...prev,
      priority: val as QueueItemDetail["priority"],
    }));
    statusMutation.mutate({ priority: val ?? null });
  };

  const handleAction = (
    action: MasteryAction,
    opts?: { keepOpen?: boolean },
  ) => {
    statusMutation.mutate({ action });
    if (opts?.keepOpen) {
      setLocalOverrides({});
    } else {
      handleClose();
    }
  };

  const handleSubmitComment = () => {
    const text = draft.trim();
    if (!text || !effectiveItemId) return;
    statusMutation.mutate({ notes: text });
    setDraft("");
  };

  const handleOpenItem = (nextItemId: string) => {
    setLocalOverrides({});
    setDraft("");
    setActiveItemId(nextItemId);
  };

  const handleClose = () => {
    setActiveItemId(null);
    setLocalOverrides({});
    setDraft("");
    onClose();
  };

  if (!effectiveItemId) return null;

  return (
    <DiffModal
      item={modalItem}
      loading={isLoading}
      comments={comments}
      auditHistory={auditHistory}
      auditHistoryLoading={auditHistoryLoading}
      onClose={handleClose}
      onOpenItem={handleOpenItem}
      draft={draft}
      onDraftChange={setDraft}
      onSubmitComment={handleSubmitComment}
      onPriorityChange={handlePriorityChange}
      onAction={handleAction}
      userFullName={userFullName}
    />
  );
}
